import { ParentApi } from "@websim/core/src/parent-api/types";
import { RenderOptions, RenderProgress } from "@websim/remotion/types";
import { Stub } from "@websim/message-proxy/client";
import { v4 as uuidv4 } from "uuid";
import {
  createAiRuntime,
  type WebsimAgentApi,
  type WebsimAi,
} from "./ai-runtime";
import { Chat } from "./chat-completion";
import { memoizePromise } from "./memoize-promise";

/** The parent-frame RPC stub the runtime is wired to: the real message-proxy
 * connection for served projects, or the sandbox shim's self-configuring one. */
export type ParentApiStub = Stub<ParentApi>;

export type WebsimComment = {
  id: string;
  content: string;
  raw_content?: string;
  created_at: string;
  author: { id: string; username: string; avatar_url: string | null } | null;
  parent_comment_id: string | null;
  reply_count: number;
  pinned: boolean;
  [key: string]: unknown;
};

export type CommentsQueryParams = {
  /** Page size (max 100). */
  first?: number;
  /** Cursor from a previous page's endCursor; prefer page.next(). */
  after?: string;
  sort_by?: "created_at" | "best";
  only_tips?: boolean;
  only_video?: boolean;
  /** Filter to a single author by user id… */
  author_id?: string;
  /** …or by username (case-insensitive). */
  author_username?: string;
  /** Case-insensitive substring search over comment content. */
  q?: string;
  /** When true, page.count holds the total matching the filters. */
  include_count?: boolean;
  /**
   * getComments only: also fetch up to N of each comment's newest replies
   * into comment.replies (a CommentsPage; use .next() for more).
   */
  include_replies?: number;
};

export type CommentsPage = {
  comments: Array<WebsimComment & { replies?: CommentsPage }>;
  /** Total comments matching the filters; null unless include_count was set. */
  count: number | null;
  hasNextPage: boolean;
  endCursor: string | null;
  /** Fetches the next page, or null when this is the last page. */
  next: (() => Promise<CommentsPage>) | null;
};

const REPLY_FETCH_CONCURRENCY = 6;

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const i = nextIndex++;
        results[i] = await fn(items[i]);
      }
    }
  );
  await Promise.all(workers);
  return results;
}

const EMPTY_COMMENTS_PAGE: CommentsPage = Object.freeze({
  comments: [],
  count: 0,
  hasNextPage: false,
  endCursor: null,
  next: null,
});

async function fetchCommentsPage(
  basePath: string,
  params: CommentsQueryParams = {}
): Promise<CommentsPage> {
  const { include_replies, ...query } = params;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      search.set(key, String(value));
    }
  }
  const queryString = search.toString();
  const response = await fetch(
    queryString ? `${basePath}?${queryString}` : basePath
  );
  if (!response.ok) {
    throw new Error(`Failed to load comments (HTTP ${response.status})`);
  }
  const body: {
    comments: {
      data: Array<{ comment: WebsimComment; cursor: string }>;
      meta: {
        count: number | null;
        has_next_page: boolean;
        end_cursor: string | null;
      };
    };
  } = await response.json();
  const { data, meta } = body.comments;
  const comments: CommentsPage["comments"] = data.map((item) => item.comment);

  if (include_replies && include_replies > 0) {
    const limit = Math.min(Math.floor(include_replies), 100);
    await mapWithConcurrency(comments, REPLY_FETCH_CONCURRENCY, async (comment) => {
      comment.replies =
        comment.reply_count > 0
          ? await fetchCommentsPage(`${basePath}/${comment.id}/replies`, {
              first: limit,
            })
          : EMPTY_COMMENTS_PAGE;
    });
  }

  const endCursor = meta.end_cursor;
  return {
    comments,
    count: meta.count ?? null,
    hasNextPage: meta.has_next_page,
    endCursor,
    next:
      meta.has_next_page && endCursor
        ? () => fetchCommentsPage(basePath, { ...params, after: endCursor })
        : null,
  };
}

type WebsimUpload = {
  (
    file: Blob,
    options: { transcode: true; postGrant: true }
  ): Promise<string | { url: string; postGrant?: string }>;
  (file: Blob, options?: { transcode?: boolean }): Promise<string>;
};

declare global {
  /** Set to true by the Builder agent's preview browser (its --init-page)
   * before page scripts run; unset everywhere else. */
  // eslint-disable-next-line no-var
  var __WEBSIM_LOW_GPU__: unknown;
  interface Window {
    websim: {
      /**
       * Upload a file; resolves with its blob URL. `{ transcode: true }`
       * re-encodes a video to H.264/AAC MP4; needs a recent click and runs
       * one at a time (a repeat of the running file shares its result).
       * `{ transcode: true, postGrant: true }` resolves `{ url, postGrant }`:
       * pass `postGrant` to the next postComment with that URL to post
       * without another click (an older host resolves a string instead).
       */
      upload: WebsimUpload;
      chat: Chat;
      /**
       * The in-page AI SDK: `const { ToolLoopAgent, tool, jsonSchema,
       * stepCountIs, generateText, streamText, generateObject } = await
       * websim.ai()` lazy-loads the Vercel AI SDK bundle; `websim.ai.model()`
       * is the project's platform-assigned model, metered by websim.
       */
      ai: WebsimAi;
      /** Sugar over the SDK's ToolLoopAgent: `await websim.agent.create({ instructions, tools })`. */
      agent: WebsimAgentApi;
      imageGen: (args: {
        prompt: string;
        aspect_ratio?: string;
        width?: number;
        height?: number;
        seed?: number;
        transparent?: boolean;
        image_inputs?: Array<{
          url: string;
        }>;
      }) => Promise<{ url: string }>;
      textToSpeech: (args: {
        text: string;
        voice?: string;
        voice_sample?: string;
        generateFile?: boolean;
      }) => Promise<{ url: string }>;
      experimental?: {
        v0: {
          login: () => Promise<void>;
          /**
           * Saves the given htmlContent to a new websim site.
           * @param htmlContent html content to save
           * @returns object with id of the saved site
           */
          save(htmlContent: string): Promise<{ id: string }>;

          /**
           * Returns the HTML for the given siteId. Defaults to the current websimsite.
           * @param siteId
           * @returns HTML for the given siteId.
           */
          getHTML(siteId?: string): Promise<string>;
        };
      };
      getUser(): ReturnType<ParentApiStub["getUser"]>;
      /** Alias for getUser */
      getCurrentUser(): ReturnType<ParentApiStub["getUser"]>;
      /**@deprecated Use getBootstrap instead */
      getDistinctId(): ReturnType<ParentApiStub["getDistinctId"]>;
      getBootstrap(): ReturnType<ParentApiStub["getBootstrap"]>;
      getCreatedBy(): ReturnType<ParentApiStub["getCreatedBy"]>;
      getCreator(): ReturnType<ParentApiStub["getCreator"]>;
      getCurrentProject(): ReturnType<ParentApiStub["getCurrentProject"]>;
      getColorScheme(): ReturnType<ParentApiStub["getColorScheme"]>;
      /**
       * True only in the Builder agent's preview browser, which draws WebGL
       * in software at a few frames a second, so a GPU-heavy game can turn
       * on its low-GPU mode there. Always false for real users.
       */
      lowGpu: boolean;
      postComment: ParentApiStub["postComment"];
      /**
       * Add (or with `remove`, retract) one emoji reaction — including the
       * "+1"/"-1" vote emojis — on a comment. Each call consumes the current
       * user gesture: one click, one reaction.
       */
      reactToComment: ParentApiStub["reactToComment"];
      /**
       * Permission for THIS project to send notifications to the signed-in
       * user's websim inbox. requestPermission consumes the current user
       * gesture and shows a websim-owned confirmation; sending itself happens
       * from the project's server.js via env.NOTIFICATIONS.send.
       */
      notifications: {
        getPermission: ParentApiStub["getNotificationPermission"];
        requestPermission: ParentApiStub["requestNotificationPermission"];
        revokePermission: ParentApiStub["revokeNotificationPermission"];
      };
      /** Paginated comments on the current project, newest first by default. */
      getComments(params?: CommentsQueryParams): Promise<CommentsPage>;
      /** Paginated replies to one comment of the current project. */
      getCommentReplies(
        comment_id: string,
        params?: Omit<CommentsQueryParams, "include_replies">
      ): Promise<CommentsPage>;
      renderVideo: RenderVideoFn;
      addEventListener: (
        eventType: "comment:created",
        callback: (data: any) => void
      ) => void;
      // TODO: Remove this and replace it with direct calls to the API
      // now that we are able to make authenticated requests.
      internal_only_experimental: Record<string, never>;
    };
  }
}

type RenderVideoFn = (args: {
  composition: string;
  inputProps?: Record<string, unknown>;
  options?: RenderOptions;
  onProgress?: (progress: RenderProgress) => void;
}) => Promise<{ url: string; renderId: string }>;

// Our legacy "<lang>-<gender>" voice codes mapped to a BCP-47 language prefix so
// we can pick a matching browser voice. Raw provider voice IDs (no dash) fall
// back to English.
const TTS_VOICE_LANG: Record<string, string> = {
  en: "en",
  es: "es",
  fr: "fr",
  de: "de",
  it: "it",
  pt: "pt",
  ru: "ru",
  hi: "hi",
  ja: "ja",
  zh: "zh",
  tl: "fil",
};

function loadSpeechVoices(
  synth: SpeechSynthesis
): Promise<SpeechSynthesisVoice[]> {
  const existing = synth.getVoices();
  if (existing.length) return Promise.resolve(existing);
  // Voices populate asynchronously on first use in most browsers.
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve(synth.getVoices());
    };
    synth.addEventListener("voiceschanged", done, { once: true });
    setTimeout(done, 1000);
  });
}

function pickSpeechVoice(
  voices: SpeechSynthesisVoice[],
  voice?: string
): SpeechSynthesisVoice | undefined {
  if (!voices.length) return undefined;
  const [langCode] = (voice ?? "en").split("-");
  const lang = TTS_VOICE_LANG[langCode] ?? "en";
  const matches = voices.filter((v) =>
    v.lang.toLowerCase().startsWith(lang.toLowerCase())
  );
  const pool = matches.length ? matches : voices;
  // Prefer Microsoft voices (the request), then the platform default.
  return (
    pool.find((v) => /microsoft/i.test(v.name)) ??
    pool.find((v) => v.default) ??
    pool[0]
  );
}

// Speak text with the browser-native Web Speech API (SpeechSynthesis). Audio
// plays directly in the iframe; there is no audio-file URL to return, so `url`
// resolves empty. Resolves when playback finishes. Voice cloning
// (`voice_sample`) is not supported by the Web Speech API and is ignored.
function speakWithWebSpeech(args: {
  text: string;
  voice?: string;
}): Promise<{ url: string }> {
  const synth =
    typeof window !== "undefined" ? window.speechSynthesis : undefined;
  if (!synth || typeof SpeechSynthesisUtterance === "undefined") {
    return Promise.reject(
      new Error("Web Speech API (speechSynthesis) is not supported here")
    );
  }
  return loadSpeechVoices(synth).then(
    (voices) =>
      new Promise<{ url: string }>((resolve, reject) => {
        const utterance = new SpeechSynthesisUtterance(args.text);
        const selected = pickSpeechVoice(voices, args.voice);
        if (selected) {
          utterance.voice = selected;
          utterance.lang = selected.lang;
        }
        utterance.onend = () => resolve({ url: "" });
        utterance.onerror = (event) =>
          reject(
            new Error(
              `Speech synthesis failed: ${event.error ?? "unknown error"}`
            )
          );
        synth.speak(utterance);
      })
  );
}

export function initRuntime(parentApi: ParentApiStub) {
  const getCurrentProject = memoizePromise(parentApi.getCurrentProject);
  const { ai, agent } = createAiRuntime(parentApi);

  const commentsBasePath = async () => {
    const project = await getCurrentProject();
    if (!project) {
      throw new Error("Comments are unavailable: no current project");
    }
    return `/api/v1/projects/${project.id}/comments`;
  };

  window.websim = Object.freeze({
    getUser: memoizePromise(parentApi.getUser),
    getCurrentUser: memoizePromise(parentApi.getUser),
    getDistinctId: memoizePromise(parentApi.getDistinctId),
    getBootstrap: memoizePromise(parentApi.getBootstrap),
    getCreatedBy: memoizePromise(parentApi.getCreatedBy),
    getCreator: memoizePromise(parentApi.getCreator),
    getCurrentProject,
    getColorScheme: parentApi.getColorScheme,
    lowGpu: globalThis.__WEBSIM_LOW_GPU__ === true,
    postComment: parentApi.postComment,
    reactToComment: parentApi.reactToComment,
    notifications: Object.freeze({
      getPermission: parentApi.getNotificationPermission,
      requestPermission: parentApi.requestNotificationPermission,
      revokePermission: parentApi.revokeNotificationPermission,
    }),
    getComments: async (params?: CommentsQueryParams) =>
      fetchCommentsPage(await commentsBasePath(), params),
    getCommentReplies: async (
      comment_id: string,
      params?: Omit<CommentsQueryParams, "include_replies">
    ) =>
      fetchCommentsPage(
        `${await commentsBasePath()}/${comment_id}/replies`,
        params
      ),
    renderVideo: (args) => {
      const id = uuidv4();

      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === "render:progress" && event.data.id === id) {
          args.onProgress?.(event.data.data);
        }
      };

      window.addEventListener("message", messageHandler);

      const renderPromise = parentApi._renderVideo(
        JSON.parse(
          JSON.stringify({
            inputProps: args.inputProps,
            composition: args.composition,
            options: args.options,
            id,
          })
        )
      );

      // Clean up the event listener when render completes or fails
      renderPromise.finally(() => {
        window.removeEventListener("message", messageHandler);
      });

      return renderPromise;
    },
    addEventListener: (
      eventType: "comment:created",
      callback: (data: any) => void
    ) => {
      // Register the parent API to start forwarding events
      void parentApi.addEventListener(eventType);

      // Listen for events from the parent window via postMessage
      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === eventType) {
          callback(event.data.data);
        }
      };

      window.addEventListener("message", messageHandler);

      // Return a cleanup function (though not currently used)
      return () => {
        window.removeEventListener("message", messageHandler);
      };
    },
    // The host resolves the object form only for { postGrant: true }.
    upload: parentApi.uploadFile as WebsimUpload,
    chat: new Chat(parentApi),
    ai,
    agent,
    imageGen: parentApi.generateImage2,
    textToSpeech: (args: {
      text: string;
      voice?: string;
      voice_sample?: string;
      generateFile?: boolean;
    }) => {
      if (args.generateFile) {
        const body = {
          text: args.text,
          voice: args.voice,
          voice_sample: args.voice_sample,
        };
        if (args.voice_sample) {
          body.voice_sample = new URL(
            args.voice_sample,
            window.location.origin
          ).toString();
        }
        return parentApi.generateTextToSpeech(body);
      }
      return speakWithWebSpeech({ text: args.text, voice: args.voice });
    },
    experimental: Object.freeze({
      v0: Object.freeze({
        login: parentApi.login,
        save(): Promise<{ id: string }> {
          throw new Error("Feature not supported");
        },
        getHTML(): Promise<string> {
          throw new Error("Feature not supported");
        },
      }),
    }),
    internal_only_experimental: {},
  });
}
